import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createManufacturingNamedVariant,
  deleteManufacturingNamedVariant,
  getManufacturingNamedVariantDetail,
  listManufacturingNamedVariants,
  updateManufacturingNamedVariant,
  type NamedVariantApplicationSummary,
  type NamedVariantStatus,
  type NamedVariantSummary,
} from "@/lib/manufacturing/namedVariantsClient";
import { slotsFromVariantConfig } from "@/lib/manufacturing/namedVariantConfigSlots";
import { namedVariantCodeFromName } from "@/lib/manufacturing/namedVariantCode";
import {
  normalizeVariantConfig,
  type VariantConfig,
} from "@/lib/manufacturing/variantConfig";
import { useNamedVariantApplicationActions } from "./useNamedVariantApplicationActions";
import { useNamedVariantApplyToRecipes } from "./useNamedVariantApplyToRecipes";

export function useManufacturingNamedVariantsTab(enabled: boolean, refreshTick: number) {
  const [variants, setVariants] = useState<NamedVariantSummary[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [applications, setApplications] = useState<NamedVariantApplicationSummary[]>([]);
  const [listBusy, setListBusy] = useState(false);
  const [detailBusy, setDetailBusy] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const [newCode, setNewCode] = useState("");
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newCodeTouched, setNewCodeTouched] = useState(false);
  const [newTransform, setNewTransform] = useState<VariantConfig | null>(null);
  const [prioritySlotLabels, setPrioritySlotLabels] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);

  const [editCode, setEditCode] = useState("");
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editStatus, setEditStatus] = useState<NamedVariantStatus>("draft");
  const [editTransform, setEditTransform] = useState<VariantConfig>({ mode: "delta" });

  const reloadList = useCallback(async () => {
    if (!enabled) return;
    setListBusy(true);
    setLocalError(null);
    const r = await listManufacturingNamedVariants();
    setListBusy(false);
    if (!r.ok) {
      setLocalError(r.error);
      return;
    }
    setVariants(r.data.variants);
  }, [enabled]);

  const reloadDetail = useCallback(async (id: string) => {
    setDetailBusy(true);
    setLocalError(null);
    const r = await getManufacturingNamedVariantDetail(id);
    setDetailBusy(false);
    if (!r.ok) {
      setLocalError(r.error);
      return;
    }
    setApplications(r.data.applications);
    setEditCode(r.data.variant.code);
    setEditName(r.data.variant.name);
    setEditDescription(r.data.variant.description);
    setEditStatus(r.data.variant.status);
    setEditTransform(r.data.variant.default_transform_config ?? { mode: "delta" });
  }, []);

  useEffect(() => {
    void reloadList();
  }, [reloadList, refreshTick]);

  useEffect(() => {
    if (!selectedId) {
      setApplications([]);
      return;
    }
    void reloadDetail(selectedId);
  }, [selectedId, reloadDetail, refreshTick]);

  const apply = useNamedVariantApplyToRecipes(
    selectedId,
    reloadDetail,
    reloadList,
    setSaveBusy,
    setLocalError,
  );

  const pickVariant = useCallback((id: string) => {
    setCreating(false);
    setPrioritySlotLabels([]);
    setSelectedId(id);
    apply.setApplyParentIds([]);
  }, [apply.setApplyParentIds]);

  const closeEditor = useCallback(() => {
    setCreating(false);
    setSelectedId(null);
    setPrioritySlotLabels([]);
    apply.setApplyParentIds([]);
  }, [apply.setApplyParentIds]);

  const beginCreate = useCallback(() => {
    setSelectedId(null);
    setCreating(true);
    setNewCode("");
    setNewName("");
    setNewDescription("");
    setNewCodeTouched(false);
    setNewTransform(null);
    setPrioritySlotLabels([]);
  }, []);

  const setNewNameAndMaybeCode = useCallback(
    (name: string) => {
      setNewName(name);
      if (!newCodeTouched) setNewCode(namedVariantCodeFromName(name));
    },
    [newCodeTouched],
  );

  const setNewCodeManual = useCallback((code: string) => {
    setNewCodeTouched(true);
    setNewCode(code);
  }, []);

  const onSeedFromExample = useCallback(
    (seed: { transform: VariantConfig; recipeSlots: string[] } | null) => {
      setNewTransform(seed?.transform ?? null);
      setPrioritySlotLabels(seed?.recipeSlots ?? []);
    },
    [],
  );

  const onCreate = useCallback(async () => {
    setSaveBusy(true);
    setLocalError(null);
    const code = newCode.trim() || namedVariantCodeFromName(newName);
    const r = await createManufacturingNamedVariant({
      code,
      name: newName,
      description: newDescription,
      status: "draft",
      ...(newTransform
        ? { default_transform_config: normalizeVariantConfig(newTransform) }
        : {}),
    });
    setSaveBusy(false);
    if (!r.ok) {
      setLocalError(r.error);
      return;
    }
    setCreating(false);
    setNewCodeTouched(false);
    setNewTransform(null);
    await reloadList();
    setSelectedId(r.data.variant.id);
  }, [newCode, newName, newDescription, newTransform, reloadList]);

  const onSaveMeta = useCallback(async () => {
    if (!selectedId) return;
    setSaveBusy(true);
    setLocalError(null);
    const r = await updateManufacturingNamedVariant({
      named_variant_id: selectedId,
      code: editCode,
      name: editName,
      description: editDescription,
      status: editStatus,
      default_transform_config: normalizeVariantConfig(editTransform),
    });
    setSaveBusy(false);
    if (!r.ok) {
      setLocalError(r.error);
      return;
    }
    await reloadList();
    await reloadDetail(selectedId);
  }, [
    selectedId,
    editCode,
    editName,
    editDescription,
    editStatus,
    editTransform,
    reloadList,
    reloadDetail,
  ]);

  const onArchive = useCallback(async () => {
    if (!selectedId) return;
    setSaveBusy(true);
    setLocalError(null);
    const r = await updateManufacturingNamedVariant({
      named_variant_id: selectedId,
      status: "archived",
    });
    setSaveBusy(false);
    if (!r.ok) {
      setLocalError(r.error);
      return;
    }
    setEditStatus("archived");
    await reloadList();
  }, [selectedId, reloadList]);

  const onDeleteVariant = useCallback(async () => {
    if (!selectedId) return;
    if (!window.confirm("Permanently delete this named variant and all of its applications?")) {
      return;
    }
    setSaveBusy(true);
    setLocalError(null);
    const r = await deleteManufacturingNamedVariant({ named_variant_id: selectedId });
    setSaveBusy(false);
    if (!r.ok) {
      setLocalError(r.error);
      return;
    }
    closeEditor();
    await reloadList();
  }, [selectedId, closeEditor, reloadList]);

  const appActions = useNamedVariantApplicationActions({
    selectedId,
    applicationsCount: applications.length,
    setSaveBusy,
    setLocalError,
    reloadDetail,
    reloadList,
  });

  const activePrioritySlots = useMemo(() => {
    const fromTransform = slotsFromVariantConfig(
      creating ? (newTransform ?? { mode: "delta" }) : editTransform,
    );
    return [...new Set([...prioritySlotLabels, ...fromTransform])];
  }, [prioritySlotLabels, creating, newTransform, editTransform]);

  return {
    variants,
    selectedId,
    applications,
    listBusy,
    detailBusy,
    saveBusy,
    applyBusy: apply.applyBusy,
    applyRecipeCount: apply.applyRecipeCount,
    localError,
    setLocalError,
    creating,
    newCode,
    setNewCode: setNewCodeManual,
    newName,
    setNewName: setNewNameAndMaybeCode,
    newDescription,
    setNewDescription,
    onSeedFromExample,
    newTransform,
    setNewTransform,
    editCode,
    setEditCode,
    editName,
    setEditName,
    editDescription,
    setEditDescription,
    editStatus,
    setEditStatus,
    editTransform,
    setEditTransform,
    activePrioritySlots,
    applyDescription: apply.applyDescription,
    setApplyDescription: apply.setApplyDescription,
    applyParentIds: apply.applyParentIds,
    setApplyParentIds: apply.setApplyParentIds,
    pickVariant,
    beginCreate,
    closeEditor,
    onCreate,
    onSaveMeta,
    onArchive,
    onDeleteVariant,
    onConfirmApplication: appActions.onConfirmApplication,
    onConfirmApplications: appActions.onConfirmApplications,
    onUpdateApplicationPrices: appActions.onUpdateApplicationPrices,
    onUpdateApplicationEffect: appActions.onUpdateApplicationEffect,
    onDeleteApplication: appActions.onDeleteApplication,
    onApplyToRecipes: apply.onApplyToRecipes,
    onResetApplications: appActions.onResetApplications,
    reloadList,
  };
}
